import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Box, Button, Container, Divider, Stack } from "@bookingcom/bui-react";
import { ArrowNavLeftIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useAuth } from "../../hooks/useAuth";
import { useUserAirlineGroupsQuery } from "../../hooks/useUserAirlineGroupsQuery";
import { parseAirlineCodes } from "../../utils/selectedAirline";
import { AirlineGroupSelector } from "../AirlineGroupSelector";
import { codesOf, isGrouped } from "../AirlineGroupSelector/selection";

export const AirlineGroupPickerHeader = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const { isAirlineUser } = useAuth();
  const { airlineCodes } = useSearch({ strict: false }) as { airlineCodes?: string };
  const { data: airlineGroups, isLoading } = useUserAirlineGroupsQuery();

  const eligibleAirlineGroups = useMemo(() => airlineGroups ?? [], [airlineGroups]);
  const eligibleAirlineCodes = useMemo(
    () => eligibleAirlineGroups.flatMap(({ airlines }) => airlines).map(({ code }) => code),
    [eligibleAirlineGroups]
  );

  const isSingleAirline = eligibleAirlineCodes.length === 1;

  // This header only renders with the aggregated flag on, so it reads airlineCodes directly
  // rather than via useSelectedAirline, whose selectedAirlineCodes ignores it when the flag is off.
  const selectedCodes = useMemo(
    () => parseAirlineCodes(airlineCodes).filter((code) => eligibleAirlineCodes.includes(code)),
    [airlineCodes, eligibleAirlineCodes]
  );

  const effectiveSelected = useMemo(() => {
    if (selectedCodes.length > 0) return selectedCodes;
    if (isAirlineUser && eligibleAirlineGroups.length === 1 && isGrouped(eligibleAirlineGroups[0])) {
      return codesOf(eligibleAirlineGroups[0]);
    }
    return [];
  }, [selectedCodes, isAirlineUser, eligibleAirlineGroups]);

  if (isSingleAirline) return null;

  // Airline users are redirected away from /overview, so the way back is admin-only.
  const showBackButton = !isAirlineUser && selectedCodes.length > 0;

  const handleSubmit = (codes: string[]) => {
    navigate({
      to: "/metrics",
      search: (prev) => ({
        ...prev,
        airlineCode: undefined,
        airlineCodes: codes.length ? codes.join(",") : undefined,
      }),
    });
  };

  return (
    <Container>
      <Box
        mixin={{
          paddingBlockStart: 8,
          paddingBlockEnd: 0,
          paddingInlineStart: 0,
          paddingInlineEnd: 0,
        }}
      >
        <Stack gap={8}>
          <Stack direction="row" alignItems="end" justifyContent="space-between">
            <AirlineGroupSelector
              groups={eligibleAirlineGroups}
              value={effectiveSelected}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              isAirlineUser={isAirlineUser}
            />
            {showBackButton && (
              <Button
                variant="secondary"
                size="large"
                icon={ArrowNavLeftIcon}
                text={i18n.trans(t("flights_airline_portal_superuser_back_button"))}
                onClick={() => {
                  navigate({ to: "/overview" });
                }}
              />
            )}
          </Stack>
          <Divider />
        </Stack>
      </Box>
    </Container>
  );
};
